import { useDispatch, useSelector } from 'react-redux';
import { Modal, Radio } from 'antd';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  RadioButton,
  Subtitle,
  Title,
} from '../../shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  defaultDateFormat,
  toMillisecond,
  toSecond,
  parseImageUrlToFileItem,
  isEmailFormat,
  numToStr,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import { toSecTimeTracks } from '../../shared/input/TimeTrackInput';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import { showFeedback } from '../../../store/ui/ui.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import {
  IFarmResource,
  IOysterLineResource,
  ISpatStorageResource,
} from '../../../entities/farms.entities';
import { loadInventories } from '../../../store/inventories/inventories.actions';
import {
  calcDiffDays,
  divideEqually,
  dozToPcs,
  pcsToDoz,
} from '../../../lib/common.helpers';
import { selectLang } from '../../../store/ui/ui.selector';
import { translate } from '../../../lib/lang.helper';
import {
  SelectAccountSetting,
  SelectCropSpeciesByFarmID,
  SelectDetailedBudgetEnabled,
  SelectFarmsByFarmID,
  SelectIsBudgetVisible,
  SelectProfileByFarmID,
  SelectSettingsByFarmID,
  SelectSpatStoragesByFarmID,
  SelectUtilsByFarmID,
} from '../../../store/extra/extra.selector';
import {
  calcOysterLineSize,
  calcOysterPercent,
  getLineOysters,
} from '../../../lib/farm.helpers';
import { OysterHarvestReseedForm } from './HarvestReseedForm';
import { IExpenseRequest } from '../../../entities/budget.entities';
import { initialBudgetForm } from '../../../lib/form.helpers';
import OperatorBudgetForm from '../OperatorBudgetForm';
import MoreFieldsForm from './MoreFieldsForm';
import { selectAccount } from '../../../store/auth/auth.selector';
import { IInventorySum } from '../../../entities/inventory.entities';
import ReturningInventoriesForm from './ReturningInventoriesForm';
import { roundValue } from '../../../entities/util-functions';
import HarvestBatchSelect, { HarvestGroupLensForm } from './HarvestBatchSelect';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { TaggingManageForm } from '../../lines/LineTagButton';
import { loadTags } from '../../../store/automation/automation.actions';
import TaxonomyGroupsForm from '../../farmform/TaxonomyGroupsForm';

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface IAmount {
  specie_id: number;
  amount?: number;
}

interface IFormData {
  is_final: boolean;
  is_full: boolean;
  is_sold?: boolean;
  season_name: string;
  amount_pcs: number | null;
  amount_dz: number | null;
  complete_date: number;
  harvest_number?: string | null;
  line_length: number | null;
  direction: string | null;
  shell_length: number | null;
  shell_length_max: number | null;
  comment?: string;
  mortality?: number;

  image_pdf_files?: IFileWall[];

  signature?: string;
  time_tracks?: ITimeRange[];
  boat_id?: number | null;
  inventories?: IInventorySum[];
  return_amount_dz: number | null;
  return_amount_pcs: number | null;
  harvest_groups?: IHarvestGroup[];
  harvest_species?: IAmount[];

  is_combined?: boolean;
  combined_batch_id?: number;

  income: number | null;
  income_details?: IExpenseRequest[];
  cost?: number;
  expenses?: IExpenseRequest[];

  measurement_units?: 'pcs' | 'dz';
  port_of_unload?: string | null;
  delivered_to?: string | null;
  packhouse?: string | null;
  crop_owner?: string | null;
  growing_area?: string | null;
  harvester_name?: string | null;

  tagged_ids?: number[] | null;
  taxonomy_group_ids: number[];
}

const defaultForm: IFormData = {
  is_final: true,
  is_full: true,
  complete_date: moment().toDate().getTime(),
  line_length: null,
  direction: null,
  season_name: '',
  amount_dz: null,
  amount_pcs: null,
  income: null,
  shell_length: null,
  shell_length_max: null,
  image_pdf_files: [],
  time_tracks: [],
  return_amount_dz: null,
  return_amount_pcs: null,
  measurement_units: 'dz',
  taxonomy_group_ids: [],
};

interface Props {
  visible: boolean;
  title?: string;
  lineData: IOysterLineResource;
  data?: any;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => Promise<any>;
  updateID?: number;
  onlyView?: boolean;
}

const OysterHarvestModal = ({
  visible,
  title,
  lineData,
  data,
  onCancel,
  onConfirm,
  overConfirm,
  updateID,
  onlyView,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const profile = SelectProfileByFarmID(lineData.farm_id);
  const onGrowWaste = SelectAccountSetting(lineData.farm_id)?.oyster_crops;
  const farmsData = SelectFarmsByFarmID(lineData.farm_id);
  const friendEmails = SelectSettingsByFarmID(lineData.farm_id)?.friend_emails;
  const farmUtils = SelectUtilsByFarmID(lineData.farm_id);
  const sizedOysters = SelectCropSpeciesByFarmID(lineData.farm_id);
  const showBudget = SelectIsBudgetVisible(lineData.farm_id);
  const detailedBudgetEnabled = SelectDetailedBudgetEnabled(lineData.farm_id);
  const weekBatches = SelectSpatStoragesByFarmID(lineData.farm_id)
    .filter(x => calcDiffDays(x.collection_date) <= 7)
    .filter(x => x.type === 'OYSTER' && !!x.harvest)
    .sort((a, b) => b.collection_date - a.collection_date);
  const hideHarvestSpecies = SelectAccountSetting(
    lineData.farm_id,
  )?.hide_harvest_species;
  const autoFillDisabled = SelectAccountSetting(
    lineData.farm_id,
  )?.auto_fill_harvest_species_disabled;

  const oysterSpecies = sizedOysters
    .map(x => {
      const totAmount = getLineOysters(lineData) ?? defaultForm.amount_dz ?? 0;
      const percent = calcOysterPercent(x, lineData.id);
      const maxAmount =
        percent === undefined ? 0 : Math.round(percent * totAmount) / 100;
      return {
        id: x.id,
        name: x.subtype ? `${x.subtype} - ${x.name}` : x.name,
        percent,
        maxAmount,
      };
    })
    .filter(x => x.percent !== 0);
  const port_of_unloads = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivered_tos = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse');
  const curFarm = farmsData.find(x => x.id === lineData.farm_id);

  const signatureCanvas = useRef<SignatureCanvas | null>();

  const [errorMsg, setErrorMsg] = useState<string>();
  const [errors, setErrors] = useState<any>();
  const [formData, setFormData] = useState<IFormData>({
    ...defaultForm,
    is_final: lineData.growing_cycle?.last_harvest
      ? !!lineData.growing_cycle.last_harvest.is_final
      : defaultForm.is_final,
    inventories: lineData.growing_cycle?.inventories_sum,
    port_of_unload:
      port_of_unloads && port_of_unloads.length > 0
        ? port_of_unloads[0].name
        : defaultForm.port_of_unload,
    delivered_to:
      lineData.delivered_to ??
      (delivered_tos && delivered_tos.length > 0
        ? delivered_tos[0].name
        : defaultForm.delivered_to),
    packhouse:
      packhouses && packhouses.length > 0
        ? packhouses[0].name
        : defaultForm.packhouse,
    crop_owner:
      curFarm && curFarm.owners && curFarm.owners.length > 0
        ? curFarm.owners[0].title
        : null,
    growing_area:
      curFarm?.growing_area ?? `${curFarm?.name} - ${lineData.line_name}`,
    harvester_name: profile?.name ?? null,
    line_length: lineData.growing_cycle
      ? lineData.growing_cycle.current_basket_count
      : defaultForm.line_length,
    amount_dz: getLineOysters(lineData) ?? defaultForm.amount_dz,
    amount_pcs: getLineOysters(lineData, 'pieces') ?? defaultForm.amount_pcs,
    shell_length: roundValue(
      calcOysterLineSize(lineData, onGrowWaste, sizedOysters)?.min,
    ),
    shell_length_max: roundValue(
      calcOysterLineSize(lineData, onGrowWaste, sizedOysters)?.max,
    ),
    harvest_groups: data?.harvest_groups ? data.harvest_groups : null,
    income_details: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
    expenses: detailedBudgetEnabled
      ? [{ name: '', quantity: null, unit_price: null, amount: null }]
      : undefined,
    harvest_species: data?.harvest_species
      ? data.harvest_species
      : oysterSpecies.map(x => ({
          specie_id: x.id,
          amount: autoFillDisabled ? undefined : x.maxAmount,
        })),
    boat_id: data?.boat_id ?? profile?.current_boat?.id,
    tagged_ids: data?.tagged_ids ?? null,
    taxonomy_group_ids:
      data?.taxonomy_group_ids && data.taxonomy_group_ids.length > 0
        ? data.taxonomy_group_ids
        : (data?.taxonomy_groups ?? []).map((x: { id: number }) => x.id),
  });

  const [sigCanLoaded, setSigCanLoaded] = useState(false);
  const [reqPdf, setReqPdf] = useState(data?.require_pdf ?? false);
  const [emails, setEmails] = useState<string>(
    data?.request_emails?.join(',') ?? friendEmails?.join(',') ?? '',
  );
  const [disabled, setDisabled] = useState(false);
  const [reseedFarms, setReseedFarms] = useState<IFarmResource[]>(
    curFarm ? [curFarm] : [],
  );
  const [reseedForm, setReseedForm] = useState<any>();

  const { currentSeed, lastHarvest } = useMemo(
    () => ({
      currentSeed: lineData.growing_cycle?.main_seed,
      lastHarvest: lineData.growing_cycle?.last_harvest,
    }),
    [lineData],
  );

  const updateSpecies = (id: number, value: string) => {
    const amount = value.length <= 0 ? undefined : Number(value);
    const tmpSpecies = formData.harvest_species?.map(x =>
      x.specie_id === id ? { ...x, amount } : { ...x },
    );
    const sum =
      Math.round(
        100 * (tmpSpecies?.reduce((p, c) => p + (c.amount ?? 0), 0) ?? 0),
      ) / 100;
    const amountDz =
      formData.measurement_units === 'pcs' ? pcsToDoz(sum ?? 0) : sum;
    const amountPcs =
      formData.measurement_units === 'pcs' ? sum : dozToPcs(sum ?? 0);
    setFormData(prv => ({
      ...prv,
      harvest_species: tmpSpecies,
      amount_dz: amountDz,
      amount_pcs: amountPcs,
    }));
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (
      [
        'income',
        'shell_length',
        'shell_length_max',
        'cost',
        'mortality',
      ].includes(String(key))
    ) {
      value = value.length <= 0 ? undefined : Number(value);
      setFormData(prv => ({ ...prv, [key]: value }));
    } else if (key === 'amount_dz') {
      value = value.length <= 0 ? undefined : Number(value);
      if (!value) {
        setFormData(prv => ({ ...prv, amount_dz: value, amount_pcs: value }));
      } else {
        setFormData(prv => ({
          ...prv,
          amount_pcs:
            formData.measurement_units == 'pcs' ? value : dozToPcs(value),
          amount_dz:
            formData.measurement_units == 'pcs' ? pcsToDoz(value) : value,
        }));
      }
    } else if (key === 'amount_pcs') {
      value = value.length <= 0 ? undefined : Number(value);
      if (!value) {
        setFormData(prv => ({ ...prv, amount_pcs: value, amount_dz: value }));
      } else {
        setFormData(prv => ({
          ...prv,
          amount_pcs:
            formData.measurement_units == 'pcs' ? value : dozToPcs(value),
          amount_dz:
            formData.measurement_units == 'pcs' ? pcsToDoz(value) : value,
        }));
      }
    } else if (key === 'return_amount_dz') {
      value = value.length <= 0 ? undefined : Number(value);
      if (!value) {
        setFormData(prv => ({
          ...prv,
          return_amount_dz:
            formData.measurement_units == 'pcs' ? pcsToDoz(value) : value,
          return_amount_pcs:
            formData.measurement_units == 'pcs' ? value : dozToPcs(value),
        }));
      } else {
        setFormData(prv => ({
          ...prv,
          return_amount_dz:
            formData.measurement_units == 'pcs' ? pcsToDoz(value) : value,
          return_amount_pcs:
            formData.measurement_units == 'pcs' ? value : dozToPcs(value),
        }));
      }
    } else if (key === 'return_amount_pcs') {
      value = value.length <= 0 ? undefined : Number(value);
      if (!value) {
        setFormData(prv => ({
          ...prv,
          return_amount_dz: pcsToDoz(value),
          return_amount_pcs: value,
        }));
      } else {
        setFormData(prv => ({
          ...prv,
          return_amount_dz: pcsToDoz(value),
          return_amount_pcs: value,
        }));
      }
    } else if (key === 'is_full') {
      setFormData(prv => ({
        ...prv,
        is_full: value,
        line_length:
          formData.inventories && formData.inventories.length > 0
            ? prv.line_length
            : value
              ? lineData.growing_cycle
                ? lineData.growing_cycle.current_basket_count
                : prv.line_length
              : prv.line_length,
        amount_dz: value
          ? (getLineOysters(lineData) ?? prv.amount_dz)
          : prv.amount_dz,
        amount_pcs: value
          ? (getLineOysters(lineData, 'pieces') ?? prv.amount_pcs)
          : prv.amount_pcs,
      }));
    } else if (key === 'line_length') {
      if (typeof value === 'string') {
        value = value.length <= 0 ? undefined : Number(value);
      }
      if (value) {
        let density = lineData.growing_cycle?.main_seed.density ?? 0;
        if (lineData.growing_cycle?.main_seed.amount_unit === 'pcs') {
          density = pcsToDoz(density);
        }
        let tmp = { ...formData };
        tmp.line_length = value;
        if (density) {
          tmp.amount_dz = Math.round(value * density);
          tmp.amount_pcs = dozToPcs(tmp.amount_dz);
          if (!autoFillDisabled) {
            const tmpSpecies = tmp.harvest_species?.map(x => {
              const spc = oysterSpecies.find(y => y.id === x.specie_id);
              if (!spc || spc.percent === undefined || tmp.amount_dz === null) {
                return x;
              }
              const amount = Math.round((spc.percent * tmp.amount_dz) / 100);
              return { ...x, amount };
            });
            tmp.harvest_species = tmpSpecies;
          }
        }
        setFormData(tmp);
      } else {
        setFormData(prv => ({ ...prv, [key]: value }));
      }
    } else if (key === 'measurement_units') {
      let tmp = { ...formData };
      if (tmp.measurement_units !== value) {
        const tmpSpecies = tmp.harvest_species?.map(x => ({
          ...x,
          amount:
            x.amount !== undefined
              ? value === 'dz'
                ? pcsToDoz(x.amount)
                : dozToPcs(x.amount)
              : undefined,
        }));
        if (tmp.mortality) {
          tmp.mortality =
            value === 'dz' ? pcsToDoz(tmp.mortality) : dozToPcs(tmp.mortality);
        }
        tmp.harvest_species = tmpSpecies;
      }
      tmp.measurement_units = value;
      setFormData(tmp);
    } else if (key === 'inventories') {
      const lengthSum = (value as IInventorySum[]).reduce(
        (p, c) => p + (c.quantity ?? 0),
        0,
      );
      let density = lineData.growing_cycle?.main_seed.density ?? 0;
      if (lineData.growing_cycle?.main_seed.amount_unit === 'pcs') {
        density = pcsToDoz(density);
      }
      let tmp = { ...formData };
      tmp.inventories = value;
      tmp.line_length = lengthSum;
      tmp.amount_dz = Math.round(lengthSum * density);
      tmp.amount_pcs = dozToPcs(tmp.amount_dz);
      if (!autoFillDisabled) {
        tmp.harvest_species = tmp.harvest_species?.map(x => {
          const spc = oysterSpecies.find(y => y.id === x.specie_id);
          if (!spc || spc.percent === undefined) {
            return x;
          }
          return {
            ...x,
            amount: Math.round((spc.percent * (tmp.amount_dz ?? 0)) / 100),
          };
        });
      }
      setFormData(tmp);
    } else if (key === 'harvest_groups') {
      const lenSum =
        value?.reduce(
          (sum: number, x: IHarvestGroup) => sum + (x.line_length ?? 0),
          0,
        ) ?? 0;
      setFormData(prv => ({
        ...prv,
        harvest_groups: value,
        line_length: lenSum > 0 ? lenSum : null,
      }));
    } else {
      if (typeof value === 'string' && value.length <= 0) {
        value = undefined;
      }
      setFormData(prv => ({ ...prv, [key]: value }));
    }
    setErrors((prv: any) => ({ ...(prv ?? {}), [key]: null }));
  };
  const toggleIsCombined = (c: boolean) => {
    const combined_batch_id =
      weekBatches.length > 0 ? weekBatches[0].id : undefined;
    setFormData(prv => ({ ...prv, is_combined: c, combined_batch_id }));
  };
  const validForm = () => {
    if (!updateID && !lineData.growing_cycle) {
      setErrorMsg('_line_empty');
      return null;
    }
    if (!updateID && !currentSeed) {
      setErrorMsg('_line_empty');
      return null;
    }
    if (
      updateID &&
      !data.is_full !== !formData.is_full &&
      lineData?.growing_cycle?.last_harvest?.id !== updateID
    ) {
      setErrorMsg('_impossible_harvest_update');
      return null;
    }
    if (formData.amount_dz === null || formData.amount_dz <= 0) {
      const es = translate(
        lang,
        formData.amount_dz === null
          ? '_enter_harvested_amt'
          : '_invalid_harvest_amount_length',
      );
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), amount: es }));
      return null;
    }
    if (
      formData.shell_length_max !== null &&
      (formData.shell_length === null ||
        formData.shell_length > formData.shell_length_max)
    ) {
      const es = translate(lang, '_invalid_shell_length_range');
      setErrorMsg(es);
      setErrors((prv: any) => ({ ...(prv ?? {}), shell_length: es }));
      return null;
    }
    if (formData.is_full) {
      const basketCnt = lineData.growing_cycle?.current_basket_count ?? 0;
      if (
        formData.line_length !== basketCnt &&
        !window.confirm(
          `${basketCnt} ${lineData.farming_method} are left but you are going to harvest ${formData.line_length} ${lineData.farming_method} as a full harvest. Are you sure?`,
        )
      )
        return null;
      const amountDz = getLineOysters(lineData) ?? 0;
      if (
        formData.amount_dz !== amountDz &&
        !window.confirm(
          `${amountDz} dozens are left but you are going to harvest ${formData.amount_dz} dozens as a full harvest. Are you sure?`,
        )
      )
        return null;
    }
    if (emails && emails.split(',').some(x => !isEmailFormat(x))) {
      setErrorMsg('_invalid_emails');
      return null;
    }
    if (reseedForm) {
      if (!formData.shell_length) {
        setErrorMsg('_input_shell_length');
        return null;
      }
      if (!reseedFarms || reseedFarms.length === 0) {
        setErrorMsg('Please select immediate seed farm');
        return null;
      }
      if (reseedForm.lines.some((x: any) => !x.basket_count)) {
        setErrorMsg('Please input number of containers for all lines');
        return null;
      }
    }
    if (
      formData.is_sold &&
      !window.confirm(translate(lang, '_sell_spat_confirm'))
    ) {
      return null;
    }
    if (!formData.line_length) {
      setErrorMsg(
        translate(lang, 'Please input %s count', lineData.farming_method),
      );
      return null;
    }
    if (formData.harvest_groups && formData.harvest_groups.length > 0) {
      let sum = 0;
      for (const group of formData.harvest_groups) {
        const seeding = lineData.growing_cycle?.seedings.find(
          x => x.id === group.batch_seed_id,
        );
        const leftLen =
          (seeding?.basket_count ?? 0) - (seeding?.harvested ?? 0);
        const existingLen = updateID
          ? (data?.harvest_groups?.find(
              (g: IHarvestGroup) => g.batch_seed_id === group.batch_seed_id,
            )?.line_length ?? 0)
          : 0;
        sum += leftLen + existingLen;
      }
      if (formData.line_length > sum) {
        setErrorMsg(
          translate(
            lang,
            '%s count is over in %s %s',
            lineData.farming_method,
            sum,
            lineData.farming_method,
          ),
        );
        return null;
      }
    }
    const divider = formData.harvest_groups?.length ?? 0;
    let res: any = {
      ...formData,
      seeding_id: data?.seeding_id ?? currentSeed?.id,
      season_name: data?.season_name ?? currentSeed?.season_name,
      line_id: lineData.id,
      complete_date: toSecond(formData.complete_date),
      require_pdf: reqPdf,
      request_emails: emails.split(','),
      return_amount_dz: formData.is_final
        ? formData.return_amount_dz
        : undefined,
      return_amount_pcs: formData.is_final
        ? formData.return_amount_pcs
        : undefined,
      mortality: formData.mortality
        ? formData.measurement_units === 'dz'
          ? dozToPcs(formData.mortality)
          : formData.mortality
        : undefined,
      reseed: reseedForm,
      harvest_groups: formData.harvest_groups?.map((x, i) => ({
        batch_seed_id: x.batch_seed_id,
        amount: divideEqually(formData.amount_pcs ?? 0, divider, i),
        line_length:
          x.line_length ?? divideEqually(formData.line_length ?? 0, divider, i),
      })),
      income_details: formData.income_details?.filter(
        x => x.name && !!x.amount,
      ),
      expenses: formData.expenses?.filter(x => x.name && !!x.amount),
      harvest_species: formData.harvest_species?.filter(x => !!x.amount),
    };
    if (!res.income) res.income = 0;
    if (
      !updateID &&
      signatureCanvas.current &&
      !signatureCanvas.current.isEmpty()
    ) {
      res.signature = signatureCanvas.current.toDataURL();
    } else {
      delete res.signature;
    }
    if (res.time_tracks && res.time_tracks.length > 0) {
      const tt = toSecTimeTracks(res.time_tracks);
      if (!tt) {
        setErrorMsg('_invalid_time_range');
        return null;
      }
      res.time_tracks = tt;
    }
    setErrors(undefined);
    return res;
  };
  const handleConfirmClick = async () => {
    if (onlyView) return;
    const form = validForm();
    if (!form) return;

    setDisabled(true);
    if (overConfirm) {
      const res = await overConfirm(form);
      if (!res.status) {
        setErrorMsg(res.data?.message ?? 'Request failed');
        if (res.data?.errors) setErrors(res.data.errors);
        setDisabled(false);
      }
      return;
    }
    const res = !updateID
      ? await sendSingleRequest(
          form,
          'POST',
          'api/farm/line/oyster-harvest',
          true,
        )
      : await sendSingleRequest(
          form,
          'PUT',
          `api/farm/line/oyster-harvest/${updateID}`,
          true,
        );
    if (res.status) {
      if (form.tagged_ids) {
        dispatch(loadTags());
      }
      dispatch(loadFarmsData());
      dispatch(loadQueueData());
      if (form.inventories) {
        dispatch(loadInventories());
      }
      onConfirm(res.data);
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
    } else {
      setErrorMsg(res.data?.message ?? 'Request failed');
      if (res.data?.errors) setErrors(res.data.errors);
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (data) {
      let tmp = { ...data };
      if (tmp.complete_date) {
        tmp.complete_date = toMillisecond(tmp.complete_date);
      }
      if (tmp.mortality && tmp.measurement_units === 'dz') {
        tmp.mortality = pcsToDoz(tmp.mortality);
      }
      if (tmp.image_pdf_files) {
        tmp.image_pdf_files = tmp.image_pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      if (tmp.pdf_files) {
        tmp.pdf_files = tmp.pdf_files.map((x: any) =>
          typeof x === 'object' && x !== null ? x : parseImageUrlToFileItem(x),
        );
      }
      if (data.time_tracks) {
        tmp.time_tracks = data.time_tracks.map((x: any) => ({
          start_time: toMillisecond(x.start_time),
          finish_time: toMillisecond(x.finish_time),
        }));
      }
      if (showBudget) {
        tmp = { ...tmp, ...initialBudgetForm(detailedBudgetEnabled, data) };
      }
      tmp.taxonomy_group_ids =
        data.taxonomy_group_ids && data.taxonomy_group_ids.length > 0
          ? data.taxonomy_group_ids
          : (data.taxonomy_groups ?? []).map((x: { id: number }) => x.id);
      setFormData(prv => ({ ...prv, ...tmp }));

      if (data.reseed && data.reseed.lines && data.reseed.lines.length > 0) {
        const reseedLineIds = new Set(
          data.reseed.lines.map((z: any) => z.line_id),
        );
        const tmpFarms = farmsData.filter(farm =>
          farm.lines?.some(line => reseedLineIds.has(line.id)),
        );
        setReseedFarms(tmpFarms);
        setReseedForm(data.reseed);
      }
    }
  }, [data, showBudget, detailedBudgetEnabled]);

  useEffect(() => {
    if (sigCanLoaded && data && data.signature) {
      signatureCanvas.current?.fromDataURL(data.signature);
    }
  }, [data, sigCanLoaded, signatureCanvas.current]);

  const getSpatName = (x: ISpatStorageResource) => {
    let line = x.source;
    line +=
      x.harvest && x.harvest.complete_date !== null
        ? ' [' + defaultDateFormat(x.harvest.complete_date) + ']'
        : '';
    return line;
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={oysterSpecies.length < 3 ? 650 : 850}
    >
      <div className='wrap'>
        <div className='d-flex align-center mb-32'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {title ?? 'Oyster harvest complete'}
          </Subtitle>
        </div>
        <div className='harvest-modal'>
          {!!errorMsg && (
            <Feedback
              message={translate(lang, errorMsg)}
              type={'error'}
              theme='light'
              position={10}
              isGlobal
              onClose={() => setErrorMsg(undefined)}
            />
          )}
          <div>
            <TaxonomyGroupsForm
              className='mb-17 mt-17'
              value={formData.taxonomy_group_ids}
              onChange={ids => updateForm('taxonomy_group_ids', ids)}
              disabled={onlyView}
            />
            {!updateID && lineData.growing_cycle && (
              <ReturningInventoriesForm
                type='OYSTER'
                farmID={lineData.farm_id}
                cycleData={lineData.growing_cycle}
                shellSize={formData.shell_length}
                form={formData.inventories}
                onChange={d => updateForm('inventories', d)}
                pastInventories={data?.inventories}
              />
            )}
            <div
              className={`${
                errors?.complete_date ? 'invalid-form' : ''
              } pb-17 px-17`}
            >
              <Datepicker
                label={translate(lang, '_harvest_complete_date')}
                defaultValue={formData.complete_date}
                onChange={e =>
                  e && updateForm('complete_date', e.toDate().getTime())
                }
              />
              {!!errors?.complete_date && (
                <div className='invalid-feedback'>{errors?.complete_date}</div>
              )}
            </div>
            <div className='d-flex mb-16 pl-17'>
              <div className='w-100'>
                {translate(lang, 'Measurement units')}
              </div>
              <div className='w-100'>
                <Radio.Group
                  className='d-flex align-center'
                  onChange={e =>
                    updateForm('measurement_units', e.target.value)
                  }
                  value={formData.measurement_units}
                >
                  <RadioButton label='dz' value='dz' />
                  <RadioButton className='ml-17' label='pcs' value='pcs' />
                </Radio.Group>
              </div>
            </div>
            <div className='pl-17 mb-32 mt-7 pr-17'>
              <Subtitle size={4} fontWeight={700} align='left' color='black-3'>
                {translate(lang, '_harvested_entire_line')}
              </Subtitle>
              <Radio.Group
                onChange={e => updateForm('is_full', e.target.value === 'F')}
                value={formData.is_full ? 'F' : 'P'}
              >
                <RadioButton
                  label={translate(lang, '_yes_line_now_empty')}
                  value='F'
                  className='mt-7'
                />
                <RadioButton
                  label={translate(lang, '_crop')}
                  value='P'
                  className='mt-7'
                />
              </Radio.Group>
            </div>
            <div className='mb-17'>
              {!!lastHarvest && lastHarvest.is_final == formData.is_final && (
                <div>
                  <Title size={6} color='black' align='center' fontWeight={500}>
                    {translate(
                      lang,
                      lastHarvest.is_final
                        ? `This is final harvest continuation`
                        : `This is intermediate harvest continuation`,
                    )}
                  </Title>
                </div>
              )}
              <div className='pl-17 mb-32 mt-7 pr-17'>
                <Radio.Group
                  onChange={e => {
                    if (e.target.value === 'SOLD') {
                      updateForm('is_sold', true);
                      updateForm('is_final', false);
                    } else {
                      updateForm('is_sold', false);
                      updateForm('is_final', e.target.value === 'FINAL');
                    }
                  }}
                  value={
                    formData.is_sold
                      ? 'SOLD'
                      : !formData.is_final
                        ? 'MIDDLE'
                        : 'FINAL'
                  }
                  disabled={onlyView || (updateID && data?.is_sold)}
                >
                  <RadioButton
                    label={translate(lang, '_radio_final')}
                    value='FINAL'
                    className='mt-7'
                  />
                  <RadioButton
                    label={translate(lang, '_radio_intermediate')}
                    value='MIDDLE'
                    className='mt-7'
                  />
                  <RadioButton
                    label={translate(lang, '_spat_sold')}
                    value='SOLD'
                    className='mt-7'
                  />
                </Radio.Group>
              </div>
              {!updateID && !formData.is_final && !formData.is_sold && (
                <OysterHarvestReseedForm
                  farmsData={farmsData}
                  farms={reseedFarms}
                  setFarms={setReseedFarms}
                  form={reseedForm}
                  setForm={setReseedForm}
                  farmId={lineData.farm_id}
                  currentStage={currentSeed?.stage}
                  disabled={onlyView || disabled}
                />
              )}
            </div>
            {(lineData.growing_cycle?.seedings.length ?? 0) > 0 &&
              !formData.is_full && (
                <div className='mb-17'>
                  <HarvestBatchSelect
                    type='OYSTER'
                    farmingMethod={lineData.farming_method}
                    growingSeeds={lineData.growing_cycle?.seedings ?? []}
                    values={
                      formData.harvest_groups?.map(x => x.batch_seed_id) ?? []
                    }
                    onChange={sIds =>
                      updateForm(
                        'harvest_groups',
                        sIds
                          ? sIds.map(id => ({
                              batch_seed_id: id,
                              line_length: formData.harvest_groups?.find(
                                x => x.batch_seed_id === id,
                              )?.line_length,
                            }))
                          : undefined,
                      )
                    }
                    showAllOptions={!!updateID}
                    existingGroups={updateID ? data?.harvest_groups : undefined}
                  />
                  <HarvestGroupLensForm
                    type='OYSTER'
                    farmingMethod={lineData.farming_method}
                    growingSeeds={lineData.growing_cycle?.seedings ?? []}
                    values={formData.harvest_groups}
                    onChange={vals => updateForm('harvest_groups', vals)}
                    existingGroups={updateID ? data?.harvest_groups : undefined}
                    className='mt-17'
                  />
                </div>
              )}
            {(!!updateID ||
              (lineData.growing_cycle?.inventories_sum.length ?? 0) <= 0) && (
              <div className='mb-17'>
                <Input
                  label={translate(
                    lang,
                    'Number of %s',
                    lineData.farming_method ?? '',
                  )}
                  type='number'
                  onChange={e => updateForm('line_length', e.target.value)}
                  value={numToStr(formData.line_length)}
                />
              </div>
            )}
            {!hideHarvestSpecies && (
              <div className='d-flex flex-wrap'>
                {oysterSpecies.map(x => {
                  const fm = formData.harvest_species?.find(
                    y => y.specie_id === x.id,
                  );
                  return (
                    <div
                      key={x.id}
                      className='mb-17 pr-12'
                      style={{
                        width: oysterSpecies.length < 3 ? '50%' : '33%',
                        boxSizing: 'border-box',
                      }}
                    >
                      <Input
                        type={'number'}
                        label={x.name}
                        value={numToStr(fm?.amount)}
                        onChange={e => updateSpecies(x.id, e.target.value)}
                        unit={formData.measurement_units ?? 'dz'}
                        disabled={onlyView}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            <div className='mb-17'>
              <div className={errors?.amount ? 'invalid-form' : ''}>
                <Input
                  label={translate(lang, 'Number of oysters')}
                  type='number'
                  onChange={e => {
                    if (formData.measurement_units == 'pcs') {
                      updateForm('amount_pcs', e.target.value);
                    } else {
                      updateForm('amount_dz', e.target.value);
                    }
                  }}
                  value={
                    formData.measurement_units == 'pcs'
                      ? numToStr(formData.amount_pcs)
                      : numToStr(formData.amount_dz)
                  }
                  unit={formData.measurement_units ?? 'dz'}
                />
                {!!errors?.amount && (
                  <div className='invalid-feedback'>{errors?.amount}</div>
                )}
              </div>
            </div>
            {!formData.is_sold && (
              <>
                {!!profile?.account?.track_mortality && (
                  <div className='mb-21'>
                    <Input
                      label={translate(lang, 'Mortality')}
                      type='number'
                      onChange={e => updateForm('mortality', e.target.value)}
                      value={numToStr(formData.mortality)}
                      unit={formData.measurement_units}
                    />
                  </div>
                )}
                <div className='mb-17'>
                  <CheckboxButton
                    checked={formData.is_combined}
                    onChange={e => toggleIsCombined(e.target.checked)}
                    label={translate(
                      lang,
                      'Combine graded oysters into daily spat',
                    )}
                  />
                </div>
                {!!formData.is_combined && (
                  <div className='mb-17'>
                    <Dropdown
                      label={translate(
                        lang,
                        '_sel_spat_storage_combine',
                      )}
                      options={weekBatches.map(x => ({
                        id: x.id.toString(),
                        value: x.id.toString(),
                        label: getSpatName(x),
                      }))}
                      value={formData.combined_batch_id?.toString()}
                      onChange={v =>
                        updateForm(
                          'combined_batch_id',
                          v ? Number(v) : undefined,
                        )
                      }
                    />
                  </div>
                )}
                {!!formData.is_final && (
                  <>
                    <div className='mb-17'>
                      <Input
                        label={translate(lang, '_graded_oysters')}
                        type='number'
                        onChange={e => {
                          if (formData.measurement_units == 'pcs') {
                            updateForm('return_amount_pcs', e.target.value);
                          } else {
                            updateForm('return_amount_dz', e.target.value);
                          }
                        }}
                        value={
                          formData.measurement_units == 'pcs'
                            ? numToStr(formData.return_amount_pcs)
                            : numToStr(formData.return_amount_dz)
                        }
                        unit={formData.measurement_units ?? 'dz'}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <div className='pb-10 d-flex justify-between'>
              <div className='w-100 mr-7'>
                <Input
                  label={translate(lang, 'Shell length from')}
                  type='number'
                  onChange={e => updateForm('shell_length', e.target.value)}
                  value={numToStr(formData.shell_length)}
                  unit={getUnitOfSpecie(unitType, 'size_mm')}
                />
              </div>
              <div className='w-100 ml-7'>
                <Input
                  label={translate(lang, 'Shell length to')}
                  type='number'
                  onChange={e => updateForm('shell_length_max', e.target.value)}
                  value={numToStr(formData.shell_length_max)}
                  unit={getUnitOfSpecie(unitType, 'size_mm')}
                />
              </div>
            </div>
            <div className='mb-17'>
              <Input
                type='text'
                onChange={e => updateForm('harvest_number', e.target.value)}
                value={formData.harvest_number ?? ''}
                label={`${translate(lang, 'Harvest number')}${
                  lineData.last_harvest?.harvest_number
                    ? ` ${translate(
                        lang,
                        'last value is %s',
                        lineData.last_harvest.harvest_number,
                      )}`
                    : ''
                }`}
                disabled={onlyView}
              />
            </div>
            <MoreFieldsForm
              type='OYSTER'
              farmID={lineData.farm_id}
              form={formData as any}
              onChange={d => setFormData(d)}
              disabled={onlyView}
            />
            {!!showBudget && (
              <OperatorBudgetForm
                detailedBudgetEnabled={detailedBudgetEnabled}
                incomeType={detailedBudgetEnabled ? 'detailed' : 'normal'}
                form={formData}
                onChange={setFormData}
                disabled={onlyView}
              />
            )}
            <div className='pb-10'>
              <Input
                label={translate(lang, 'Comment')}
                type='textarea'
                onChange={e => updateForm('comment', e.target.value)}
                value={formData.comment ?? ''}
              />
            </div>
            <div className='pb-10'>
              <FilesWall
                label={translate(lang, 'PDF/Images')}
                fileList={formData.image_pdf_files}
                onChange={v => updateForm('image_pdf_files', v)}
              />
            </div>
            <div className='pb-20'>
              <p className='mb-4 d-block paragrapgh paragrapgh--2 paragrapgh--400 paragrapgh--default'>
                {translate(lang, 'Signature')}
              </p>
              <div
                style={{
                  border: '1px solid #EFEFEF',
                  borderRadius: '4px',
                  height: '200px',
                }}
              >
                <SignatureCanvas
                  ref={r => {
                    signatureCanvas.current = r;
                    setSigCanLoaded(true);
                  }}
                  penColor='green'
                  canvasProps={{ width: 550, height: 200 }}
                />
              </div>
            </div>
            {!updateID && !formData.is_sold && (
              <div className='mb-27'>
                <CheckboxButton
                  checked={reqPdf}
                  onChange={e => setReqPdf(e.target.checked)}
                  label={translate(
                    lang,
                    '_send_me_pdf_harvest',
                  )}
                />
                {reqPdf && (
                  <div className='mt-17'>
                    <Input
                      label={translate(lang, 'Email list')}
                      placeholder={translate(
                        lang,
                        '_enter_emails_separate_comma',
                      )}
                      type='text'
                      value={emails}
                      onChange={e => setEmails(e.target.value)}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {!updateID && (
        <TaggingManageForm
          className='mb-17 mt-17'
          lineData={lineData}
          tagIDs={formData.tagged_ids}
          onChange={ids => updateForm('tagged_ids', ids)}
        />
      )}
      <div className='modal-button d-flex justify-end align-center'>
        <Button
          width={'small'}
          size={2}
          type='fill'
          color='green'
          className='rsp-btn ml-16'
          onClick={handleConfirmClick}
          disabled={disabled || onlyView}
        >
          {translate(lang, 'Confirm')}
        </Button>
      </div>
    </Modal>
  );
};

export default OysterHarvestModal;
